/**
 * Shared "where is this person in their lifecycle" classifier.
 *
 * Used by PeoplePanel (admin) and ClientManagement (coach) so the two lists
 * bucket people the same way and can't drift apart. See
 * src/migrations/054_add_user_archive_state.sql for the archived_at model.
 *
 * Buckets:
 *   members      - a current, paying/active member (or comped, or trialing)
 *   needs_action - something is waiting on the coach/admin
 *   staff        - coach / super_admin accounts
 *   inactive     - archived, suspended, rejected, or a never-activated signup
 *
 * The default list view shows members + needs_action + staff; `inactive` is
 * tucked behind its own tab.
 *
 * @param {object} p normalized person. Recognised keys (all optional):
 *   archived_at, archive_reason, role, status, needs_review,
 *   subscription_status, open_flags, is_complimentary, nutrition_tier
 * @returns {'members'|'needs_action'|'staff'|'inactive'}
 */
export function getLifecycleBucket(p = {}) {
  if (p.archived_at) return 'inactive';

  if (p.role === 'coach' || p.role === 'admin' || p.role === 'super_admin') return 'staff';

  if (p.status === 'suspended' || p.status === 'rejected') return 'inactive';

  if (
    p.needs_review ||
    p.status === 'pending' ||
    p.subscription_status === 'past_due' ||
    (p.open_flags ?? 0) > 0
  ) {
    return 'needs_action';
  }

  const hasAccess =
    !!p.is_complimentary ||
    p.subscription_status === 'active' ||
    p.subscription_status === 'trialing' ||
    (!!p.nutrition_tier && p.nutrition_tier !== 'free');
  if (hasAccess) return 'members';

  // Signed up, no access, nothing pending — dead weight.
  return 'inactive';
}

/**
 * Short human reason a person is in the `inactive` bucket, for display on the
 * Inactive tab. Returns null for non-inactive people.
 */
export function getInactiveReason(p = {}) {
  if (getLifecycleBucket(p) !== 'inactive') return null;

  if (p.archived_at) {
    const label = {
      subscription_canceled: 'Subscription canceled',
      rejected_signup: 'Rejected signup',
      inactive_never_started: 'Never activated',
      manual: 'Archived',
    }[p.archive_reason] || 'Archived';
    return label;
  }
  if (p.status === 'suspended') return 'Suspended';
  if (p.status === 'rejected') return 'Rejected';
  if (p.subscription_status === 'canceled') return 'Subscription canceled';
  return 'Never activated';
}

/** True when this person can still be archived (i.e. isn't already, isn't staff). */
export function canArchive(p = {}) {
  if (p.archived_at) return false;
  return !(p.role === 'coach' || p.role === 'admin' || p.role === 'super_admin');
}

export const LIFECYCLE_TABS = [
  { key: 'members',      label: 'Members' },
  { key: 'needs_action', label: 'Needs action' },
  { key: 'staff',        label: 'Staff' },
  { key: 'inactive',     label: 'Inactive' },
  { key: 'all',          label: 'All' },
];
